import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Scroll management on navigation.
 *
 * Without this, following a link from halfway down a long article lands you halfway
 * down the next page.
 *
 * Hash links need explicit handling now that routes are lazily loaded: the browser
 * tries to scroll to #target while the route chunk is still downloading, finds
 * nothing, and gives up. So retry across a few frames until the element exists.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      return
    }

    const id = decodeURIComponent(hash.slice(1))
    let raf = 0
    let attempts = 0

    const tryScroll = () => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'instant', block: 'start' })
        return
      }
      // ~1s at 60fps: enough for a lazy chunk on a slow connection, then give up.
      if (attempts++ < 60) raf = requestAnimationFrame(tryScroll)
    }

    raf = requestAnimationFrame(tryScroll)
    return () => cancelAnimationFrame(raf)
  }, [pathname, hash])

  return null
}

export default ScrollToTop
